import React from 'react';
import theme from 'src/config/theme';
import { BookWithChapters } from '../../types';
import { SpriteIconsLight, SpriteIconsRegular } from 'src/config/api/models/FontAwesome';
import { FontAwesomeSpriteIcon } from 'src/modules/shared/components/FontAwesomeLocal';
import { ITrajectoryDetail } from 'src/config/api/models/Trajectory';
import { Block, BlockType } from 'src/config/api/models/Block';
import { ChapterContentWrapper } from './Atoms';
import { NavbarContent } from '../../slices/navigationBar/type';
import { IBookChapter } from 'src/config/api/models/Chapter';

const activecolor = theme.palette.primary.main;

const flatChapters = (chapters: IBookChapter[]) => {
  let flattenChapters: IBookChapter[] = [];
  
  for (const chapter of chapters) {
    flattenChapters.push(chapter);

    if (chapter.childrenChapters)
      flattenChapters = flattenChapters
        .concat(flatChapters(chapter.childrenChapters))
  }

  return flattenChapters;
}

const findBaseChapter = (chapter: IBookChapter, allChapters: IBookChapter[]) => {
  const parentChapter = allChapters
    .find(item => item.childrenChapters.some(childChapter => childChapter.id === chapter.id));

  if (!parentChapter) return chapter;
  return findBaseChapter(parentChapter, allChapters);
}

const findChapter = (
  bookInfo: BookWithChapters | undefined,
  id: string) => {
    if (!bookInfo) return;

    const allChapters = flatChapters(bookInfo.chapters);
    const matchedChapter = allChapters.find(item => item.id === id);
    if (matchedChapter) {
      const baseChapter = findBaseChapter(matchedChapter, allChapters);
      return baseChapter;
    } 

    return;
}

const getChapterContent = (
  bookInfo: BookWithChapters | undefined,
  chapterId: string,
  onClick: () => void
) => {
  const chapter = findChapter(bookInfo, chapterId);
  const navbarContent: NavbarContent = {
    type: 'chapter',
    id: chapter?.id ?? '',
    firstItem: `H${chapter?.number}`,
    secondItem: (
      <ChapterContentWrapper>
        <div>Hoofdstuk {chapter?.number}</div>
        <div className="chapter-title" style={{ textOverflow: 'ellipsis', overflow: 'hidden' }}>
          {chapter?.title ?? ''}
        </div>
      </ChapterContentWrapper>
    ),
    onClick: () => onClick(),
    tooltip: chapter?.title ?? '',
    activecolor,
  };
  return navbarContent;
};

export function getContentToMap(
  bookInfo: BookWithChapters | undefined,
  handleBookClick: () => void,
  handleChapterClick: () => void,
  chapterId: string,
  trajectory: ITrajectoryDetail | undefined,
  scroolToTopClick: (id: string, type?: BlockType) => void,
  blocks: Block[] | undefined
) {
  let content: NavbarContent[] = [];

  if (bookInfo) {
    content = [
      {
        type: 'book',
        id: bookInfo.id,
        firstItem: (
          <>
            <FontAwesomeSpriteIcon
              color={theme.palette.secondary.main}
              icon={SpriteIconsRegular.CHEVRONS_RIGHT}
              iconSize="md"
            />
          </>
        ),
        secondItem: bookInfo.title,
        onClick: () => handleBookClick(),
        activecolor,
      },
    ];
  }

  if (chapterId !== null && bookInfo) {
    const chapterContent = getChapterContent(bookInfo, chapterId, handleChapterClick);
    content.push(chapterContent);
  }

  if (trajectory) {
    content = [
      ...content,
      {
        type: 'trajectory',
        id: trajectory.id,
        firstItem: trajectory.number ? trajectory.number : '',
        secondItem: trajectory.parentTitle ? trajectory.parentTitle : trajectory.title,
        onClick: () => scroolToTopClick('private_page_wrapper'),
        tooltip: trajectory.title,
        activecolor,
      },
    ];
  }
  content = [
    ...content,
    {
      type: 'goals',
      id: 'goals',
      firstItem: (
        <FontAwesomeSpriteIcon
          color={theme.config.appbarColors.icon}
          icon={SpriteIconsLight.BULLSEYE_ARROW}
          iconSize="md"
        />
      ),
      secondItem: 'Doelen',
      onClick: () => scroolToTopClick('private_page_wrapper'),
      activecolor,
    },
  ];
  if (blocks) {
    const newBlockls = blocks
      .filter((item: Block) => item.type === BlockType['CLUSTER'])
      .map((mapItem, mapIndex) => {
        return {
          type: 'blocks',
          id: mapItem.id,
          firstItem: (mapIndex + 1).toString(),
          secondItem: mapItem.title ? mapItem.title : '',
          onClick: () => scroolToTopClick(`trajectory_block_${mapItem.id}`, mapItem.type),
          tooltip: mapItem.title ? mapItem.title : '',
          activecolor,
        };
      });

    content = [...content, ...newBlockls];
  }

  return content;
}
